import React from "react";
import { Button, Card, Popconfirm, Space, Switch, Tag, Tooltip } from "antd";
import { DeleteOutlined, RedoOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";

import FlyerBannerSection from "./FlyerBannerSection.jsx";
import FlyerProductSection from "./FlyerProductSection.jsx";

export default function FlyerCardEditor({
	row,
	categoryOptions,
	categoryLoading = false,
	classOptions = [],
	classLoading = false,
	badgeOptions = [],
	badgeLoading = false,
	filterOptions = [],
	filterLoading = false,
	mechanicOptions = [],
	mechanicLoading = false,
	fundOptions = [],
	fundLoading = false,
	productFields = [],
	productFieldsLoading = false,
	genericTaxonomies = {},
	saving,
	dirty,
	onFieldChange,
	onSave,
	onReset,
	onDelete,
	onReprocess,
	isReprocessing = false,
	reprocessDisabled = false,
	onToggleValidated,
	onCreateCategory,
	onCreateClass,
	onCreateFund,
	onCreateBadge,
	onCreateFilter,
	onCreateMechanic,
	getImageUrl,
	onServerCardUpdate,
	availablePages = [],
	pendingBannerAssets = {},
	onPendingAssetChange,
	loading = false,
}) {
	const isSaving = !!saving?.[row._id];
	const dirtyFields = dirty?.[row._id] || {};
	const isDirty = Object.keys(dirtyFields).length > 0;
	const isValidated = row?.status === "validated";
	// Durante la rielaborazione AI campi e azioni di scrittura sono bloccati: l'OCR sta riscrivendo
	// i dati della card, così evitiamo modifiche concorrenti che andrebbero perse.
	const fieldsLocked = isSaving || isValidated || isReprocessing;

	const handleField = React.useCallback(
		(field, value) => {
			onFieldChange?.(row._id, field, value);
		},
		[onFieldChange, row?._id],
	);

	const handleBannerField = React.useCallback(
		(field, value) => {
			onFieldChange?.(row._id, `banner.${field}`, value);
		},
		[onFieldChange, row?._id],
	);

	const [isDeleting, setIsDeleting] = React.useState(false);

	const handleSave = React.useCallback(() => onSave?.(row), [onSave, row]);
	const handleReset = React.useCallback(() => onReset?.(row), [onReset, row]);
	const handleDelete = React.useCallback(async () => {
		setIsDeleting(true);
		try {
			await onDelete?.(row);
		}
		finally {
			setIsDeleting(false);
		}
	}, [onDelete, row]);
	const handleToggle = React.useCallback(
		(checked) => onToggleValidated?.(row, checked),
		[onToggleValidated, row],
	);

	const handlePendingAssetChange = React.useCallback(
		(assetType, payload) => {
			onPendingAssetChange?.(row._id, assetType, payload);
		},
		[onPendingAssetChange, row?._id],
	);

	const imageUrl = React.useMemo(
		() =>
			typeof getImageUrl === "function"
				? getImageUrl(row?.relPath, row?.updatedAt)
				: row?.relPath,
		[getImageUrl, row?.relPath, row?.updatedAt],
	);

	const gabbiaType = React.useMemo(() => String(row?.typeGabbia || "").toLowerCase(), [row?.typeGabbia]);
	const showProductSection = gabbiaType === "gabbia";
	const bannerData = React.useMemo(() => row?.banner || {}, [row?.banner]);

	return (
		<Card className="fm-card" loading={loading} data-card-id={row?._id || row?.id}>
			<div className="fm-card-body">
				<div className="fm-fields">
					{
						showProductSection
							? (
								<FlyerProductSection
									value={row}
									onChange={handleField}
									categoryOptions={categoryOptions}
									categoryLoading={categoryLoading}
									onCreateCategory={onCreateCategory}
									classOptions={classOptions}
									classLoading={classLoading}
									onCreateClass={onCreateClass}
									fundOptions={fundOptions}
									fundLoading={fundLoading}
									onCreateFund={onCreateFund}
									badgeOptions={badgeOptions}
									badgeLoading={badgeLoading}
									onCreateBadge={onCreateBadge}
									filterOptions={filterOptions}
									filterLoading={filterLoading}
									onCreateFilter={onCreateFilter}
									mechanicOptions={mechanicOptions}
									mechanicLoading={mechanicLoading}
									onCreateMechanic={onCreateMechanic}
									productFields={productFields}
									productFieldsLoading={productFieldsLoading}
									genericTaxonomies={genericTaxonomies}
									disabled={fieldsLocked}
									onServerCardUpdate={onServerCardUpdate}
									availablePages={availablePages}
								/>
							)
							: (<FlyerBannerSection
								rowId={row._id}
								value={bannerData}
								onChange={handleBannerField}
								onPendingAssetChange={handlePendingAssetChange}
								pendingAssets={pendingBannerAssets}
								disabled={isSaving || isValidated}
								seriale={row?.seriale}
								categoryOptions={categoryOptions}
								categoryLoading={categoryLoading}
								onCreateCategory={onCreateCategory}
							/>)}

					{!isValidated && (
						<div className="fm-actions">
							<Space>
								<Button
									type="primary"
									size="small"
									icon={<SaveOutlined />}
									disabled={!isDirty || isReprocessing}
									loading={isSaving}
									onClick={handleSave}
								>
									Salva
								</Button>
								<Button
									size="small"
									icon={<ReloadOutlined />}
									disabled={!isDirty || isReprocessing}
									onClick={handleReset}
								>
									Reset
								</Button>
								<Popconfirm
									title="Eliminare questa card?"
									description="L'azione è irreversibile."
									onConfirm={handleDelete}
									okText="Elimina"
									cancelText="Annulla"
									okButtonProps={{ danger: true }}
								>
									<Button
										size="small"
										danger
										disabled={isReprocessing}
										icon={<DeleteOutlined />}
										loading={isDeleting}
									>
										Elimina
									</Button>
								</Popconfirm>
								{showProductSection && onReprocess && (
									<Tooltip title={!row?.seriale
										? "Card senza seriale: rielaborazione non disponibile"
										: "Rielabora i dati AI di questa card con l'OCR"}>
										<Button
											size="small"
											icon={<RedoOutlined />}
											loading={isReprocessing}
											disabled={!row?.seriale || (reprocessDisabled && !isReprocessing)}
											onClick={() => onReprocess?.(row)}
										>
											Rielabora con AI
										</Button>
									</Tooltip>
								)}
							</Space>
						</div>
					)}
				</div>

				<div className="fm-preview">
					<div className="fm-thumb">
						<img src={imageUrl} alt="Anteprima" draggable="false" />
					</div>
					<div className="fm-status">
						{row?.source && row.source !== "new" && (
							<Tag
								color={row.source === "inherited" ? "orange" : "yellow"}
								bordered={false}
								style={{fontSize: 10}}
							>
								{row.source === "inherited" ? "Ereditata" : "Rielaborata"}
							</Tag>
						)}
						<Tag
							color={row?.status === "validated" ? "green" : "gold"}
							bordered={false}
						>
							{row?.status === "validated" ? "Validata" : "Da validare"}
						</Tag>
						<Tooltip
							title={isDirty ? "Le modifiche verranno salvate automaticamente prima di validare" : null}
						>
							<Switch
								checkedChildren="OK"
								unCheckedChildren="TODO"
								size="small"
								checked={isValidated}
								loading={isSaving}
								disabled={isReprocessing}
								onChange={handleToggle}
							/>
						</Tooltip>
					</div>
				</div>
			</div>
		</Card>
	);
}
